import React from 'react';
import clsx from 'clsx';
import type { SerializedProfileAsset } from 'fym-shared/src/music24/artistProfile';
import type { ProfileSendItem } from 'fym-shared/src/music24/profileSend';
import { TypedMemo } from '../../../../common/types';
import { imgproxySrc } from '../../../../common/drawnImageSrc';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Send to all sheet; each part uses its own slice of it */
import styles from './SendAll.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The CSS width of a wide thumbnail and the side of a square one (design c `.th`). */
const WIDE_PX = 72;
const SQUARE_PX = 34;

/**
 * The imgproxy address of the thumbnail, or null. Owner rule 2026-10-19: never the `/download`
 * route in an `<img>`; with no `sourceUrl` (an old API) the box stays empty. Pure.
 */
export function sendAllThumbSrc(asset: SerializedProfileAsset | null, square: boolean): string | null {
  if (asset === null || asset.fileRemoved || asset.sourceUrl === undefined || asset.sourceUrl === null) {
    return null;
  }
  return imgproxySrc(asset.sourceUrl, square ? SQUARE_PX : WIDE_PX, { square });
}

interface SendAllThumbProps {
  item: ProfileSendItem;
  asset: SerializedProfileAsset | null;
}

/** The preview of an item (design c `.th`): the image of the version, "Aa" for a text, "Links" for a link. */
const SendAllThumbComponent: React.FC<SendAllThumbProps> = ({ item, asset }) => {
  if (item === 'bio' || item === 'facebookLink') {
    return (
      <span className={styles.thumb} aria-hidden="true">
        <span className={styles.thumbText}>{item === 'bio' ? 'Aa' : 'Links'}</span>
      </span>
    );
  }
  const square = item === 'picture';
  const src = sendAllThumbSrc(asset, square);
  return (
    <span className={styles.thumb} aria-hidden="true">
      {src === null ? (
        <span className={clsx(square ? styles.thumbPhoto : styles.thumbWide, styles.thumbEmpty)} />
      ) : (
        <img
          className={square ? styles.thumbPhoto : styles.thumbWide}
          src={src}
          alt=""
          loading="lazy"
          data-testid={`send-all-thumb-${item}`}
        />
      )}
    </span>
  );
};

export const SendAllThumb = TypedMemo(SendAllThumbComponent);
